import { useRouter } from 'next/router'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Button } from '@/components/Button/Button'
import { Icon } from '@/components/Icon/Icon'
import { GOOGLE_EVENTS, META_EVENTS, trackEvent, useMetaTrack } from '@/shared/analytics'
import { extractApiError } from '@/shared/api/extractApiError'
import { useAccountAuthMagicLinkRequestCreate } from '@/shared/api/generated/authentication/authentication'
import { usePrintPresetsList } from '@/shared/api/generated/print-dimensions/print-dimensions'
import { publicStoriesDetail } from '@/shared/api/generated/public-stories/public-stories'
import { storefrontSessionsChildrenCreate } from '@/shared/api/generated/storefront-children/storefront-children'
import {
  storefrontSessionsClaimAndGenerateCreate, storefrontSessionsConfigurePremadeCreate, storefrontSessionsCreate,
  storefrontSessionsPremadeCoverPreviewCreate, storefrontSessionsPremadeCoverPreviewRetrieve, storefrontSessionsRetrieve,
} from '@/shared/api/generated/storefront-sessions/storefront-sessions'
import { usePixieWorldAuthContext } from '@/shared/auth/pixieWorldAuthContext'
import { CustomImage } from '@/shared/components/CustomImage/CustomImage'
import { QuillIcon } from '@/shared/components/QuillIcon/QuillIcon'
import { TextField } from '@/shared/components/TextField/TextField'
import { getLanguageFlag } from '@/shared/libs/languageFlag'
import { pxToRem } from '@/shared/styles/themes'
import { PhotoTrustDialog } from '@/slices/Onboarding/components/PhotoTrustDialog'
import { PhotoUploadWidget } from '@/slices/Onboarding/components/widgets/PhotoUploadWidget'
import { BookFormatSelector } from '@/slices/StoryCreation/components'
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons/faArrowLeft'
import { faArrowRight } from '@fortawesome/pro-light-svg-icons/faArrowRight'
import { faCheckCircle } from '@fortawesome/pro-light-svg-icons/faCheckCircle'
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons/faCircleInfo'
import { faEnvelope } from '@fortawesome/pro-light-svg-icons/faEnvelope'
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark'
import { Alert, Box, CircularProgress, Container, IconButton, LinearProgress, styled, Tab, Tabs, Typography } from '@mui/material'

import { PremadeCharacterPicker } from './PremadeCharacterPicker'

import type {
  AddChild, AgeGroupEnum, Gender881Enum, PremadeStoryDetail, PremadeStoryLook, PremadeStoryRole,
} from '@/shared/api/models'

type RoleInputMode = 'pick' | 'upload'

type RoleInput = {
  mode: RoleInputMode
  photoFile: File | null
  photoPreviewUrl: string | null
  lookId: number | null
}

const EMPTY_ROLE_INPUT: RoleInput = {
  mode: 'pick',
  photoFile: null,
  photoPreviewUrl: null,
  lookId: null,
}

interface PremadePersonaliseWizardProps {
  story: PremadeStoryDetail
}

type Step = 'photo' | 'child_info' | 'language' | 'format' | 'preview' | 'email' | 'email_sent' | 'claiming'

const COVER_GENERATION_ESTIMATE_MS = 35000
const DEFAULT_COVER_ASPECT_RATIO = 0.74

type AgeGroupOption = { value: AgeGroupEnum; label: string; description: string }

const AGE_GROUP_OPTIONS: AgeGroupOption[] = [
  { value: 'newborn', label: 'Newborn', description: '0-12 months' },
  { value: 'toddler', label: 'Toddler', description: '1-3 years' },
  { value: 'child', label: 'Child', description: '3-6 years' },
  { value: 'parent', label: 'Parent', description: 'Adult' },
]

const AGE_GROUP_TO_AGE: Record<AgeGroupEnum, number> = {
  newborn: 1,
  toddler: 2,
  child: 5,
  parent: 30,
}

const STEP_TITLES: Record<Step, string> = {
  photo: 'Add a photo',
  child_info: 'Tell us about the star',
  language: 'Choose your language',
  format: 'Choose your book size',
  preview: 'Here\'s a sneak peek',
  email: 'One last step — claim your free story',
  email_sent: 'Check your email',
  claiming: 'Creating your story...',
}

export const PremadePersonaliseWizard: React.FC<PremadePersonaliseWizardProps> = ({ story }) => {
  const router = useRouter()
  const trackMeta = useMetaTrack()
  const { isAuthenticated } = usePixieWorldAuthContext()

  const [step, setStep] = useState<Step>('photo')
  const [roleInputs, setRoleInputs] = useState<Record<string, RoleInput>>({})
  const [childName, setChildName] = useState('')
  const [childAgeGroup, setChildAgeGroup] = useState<AgeGroupEnum | ''>('')
  const [selectedLanguageCode, setSelectedLanguageCode] = useState<string>('en')
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [emailInput, setEmailInput] = useState('')
  const requestMagicLink = useAccountAuthMagicLinkRequestCreate()

  // Fires once after the user commits to the first wizard step (photo/character
  // pick). Filters out cold-bounce traffic so the resulting retargeting
  // audience and lookalike seed reflect real configurator engagement.
  const customizeProductFiredRef = useRef(false)

  // Session + preview state
  const [sessionUuid, setSessionUuid] = useState<string | null>(null)
  const [isPreparingPreview, setIsPreparingPreview] = useState(false)
  const [coverStatus, setCoverStatus] = useState<
  'idle' | 'pending' | 'generating' | 'ready' | 'failed'
  >('idle')
  const [personalisedCoverUrl, setPersonalisedCoverUrl] = useState<string | null>(null)
  const [sampleCoverUrl, setSampleCoverUrl] = useState<string | null>(null)

  // Book format selection (drives sneak-peek aspect ratio)
  const [selectedPresetId, setSelectedPresetId] = useState<number | null>(null)
  const { data: presetsResponse, isLoading: isPresetsLoading } = usePrintPresetsList()
  const bookFormatPresets = useMemo(() => {
    const presets = presetsResponse?.results ?? []
    const hardcover = presets.filter((p) => p.cover_type === 'hardcover')

    return hardcover.length > 0 ? hardcover : presets
  }, [presetsResponse])

  useEffect(() => {
    if (selectedPresetId !== null) return
    if (bookFormatPresets.length === 0) return

    const def = bookFormatPresets.find((p) => p.is_default) ?? bookFormatPresets[0]

    setSelectedPresetId(def.id)
  }, [bookFormatPresets, selectedPresetId])

  const coverAspectRatio = useMemo(() => {
    const preset = bookFormatPresets.find((p) => p.id === selectedPresetId)
    const w = parseFloat(preset?.page_width_mm ?? '0')
    const h = parseFloat(preset?.page_height_mm ?? '0')

    if (!w || !h) return DEFAULT_COVER_ASPECT_RATIO

    return w / h
  }, [bookFormatPresets, selectedPresetId])

  // Progress bar while the personalised cover is generating.
  const [progressValue, setProgressValue] = useState(0)
  const generationStartRef = useRef<number | null>(null)

  // Each entry in slide_previews_data is a content spread (2 pages).
  // Covers and inside pages are added at generation time and not listed here.
  const insidePagesCount = useMemo(() => {
    const slides = (story.slide_previews_data as unknown[] | undefined)?.length

    return slides ? slides * 2 : null
  }, [story.slide_previews_data])

  const allowedAgeGroups = useMemo(() => {
    const supported = (story.supported_age_groups as string[] | undefined) ?? []

    if (!supported.length) return AGE_GROUP_OPTIONS

    return AGE_GROUP_OPTIONS.filter((o) => supported.includes(o.value))
  }, [story.supported_age_groups])

  const availableLanguages = useMemo(
    () => story.available_languages ?? [{ code: 'en', name_english: 'English', name_native: 'English' }],
    [story.available_languages],
  )
  const hasLanguageChoice = availableLanguages.length > 1

  // Roles the user must fill in (one for single-character stories,
  // multiple for stories like mother's-day-letter once authored).
  const requiredLookRoles = useMemo<PremadeStoryRole[]>(() => {
    const roles = (story.character_roles ?? []) as readonly PremadeStoryRole[]

    return [...roles]
      .filter((r) => r.is_required && r.requires_child_input)
      .sort((a, b) => a.sort_order - b.sort_order)
  }, [story.character_roles])

  // Group story.looks by role_key so the picker can render the right options
  // per role section. Looks are sorted by display_order within each role.
  const looksByRole = useMemo<Record<string, PremadeStoryLook[]>>(() => {
    const map: Record<string, PremadeStoryLook[]> = {}

    for (const look of (story.looks ?? []) as readonly PremadeStoryLook[]) {
      if (!map[look.role_key]) map[look.role_key] = []

      map[look.role_key].push(look)
    }

    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => a.display_order - b.display_order)
    }

    return map
  }, [story.looks])

  // Initialise per-role input state once roles are known. Default to 'pick'
  // when looks exist for that role (privacy-first), otherwise 'upload'.
  useEffect(() => {
    if (requiredLookRoles.length === 0) return

    setRoleInputs((prev) => {
      const next = { ...prev }
      let changed = false

      for (const role of requiredLookRoles) {
        if (next[role.role_key]) continue

        const hasLooks = (looksByRole[role.role_key]?.length ?? 0) > 0

        next[role.role_key] = {
          ...EMPTY_ROLE_INPUT,
          mode: hasLooks ? 'pick' : 'upload',
        }
        changed = true
      }

      return changed ? next : prev
    })
  }, [requiredLookRoles, looksByRole])

  const primaryRoleKey = requiredLookRoles[0]?.role_key ?? 'protagonist'

  const setRoleInputMode = useCallback((roleKey: string, mode: RoleInputMode) => {
    setRoleInputs((prev) => ({
      ...prev,
      [roleKey]: { ...(prev[roleKey] ?? EMPTY_ROLE_INPUT), mode },
    }))
  }, [])

  const setRolePhoto = useCallback((roleKey: string, file: File, previewUrl: string) => {
    setRoleInputs((prev) => ({
      ...prev,
      [roleKey]: {
        ...(prev[roleKey] ?? EMPTY_ROLE_INPUT),
        mode: 'upload',
        photoFile: file,
        photoPreviewUrl: previewUrl,
        lookId: null,
      },
    }))
  }, [])

  const clearRolePhoto = useCallback((roleKey: string) => {
    setRoleInputs((prev) => ({
      ...prev,
      [roleKey]: {
        ...(prev[roleKey] ?? EMPTY_ROLE_INPUT),
        photoFile: null,
        photoPreviewUrl: null,
      },
    }))
  }, [])

  const setRoleLook = useCallback((roleKey: string, lookId: number) => {
    setRoleInputs((prev) => ({
      ...prev,
      [roleKey]: {
        ...(prev[roleKey] ?? EMPTY_ROLE_INPUT),
        mode: 'pick',
        lookId,
        photoFile: null,
        photoPreviewUrl: null,
      },
    }))
    trackEvent(GOOGLE_EVENTS.CHARACTER_SELECTED, {
      item_id: story.slug,
      role_key: roleKey,
      look_id: lookId,
    })
  }, [story.slug])

  const isRoleReady = useCallback((role: PremadeStoryRole) => {
    const input = roleInputs[role.role_key]

    if (!input) return false
    if (input.mode === 'upload') return !!input.photoFile

    return !!input.lookId
  }, [roleInputs])

  const canLeavePhotoStep = requiredLookRoles.length > 0
    && requiredLookRoles.every(isRoleReady)

  useEffect(() => {
    trackEvent(GOOGLE_EVENTS.BEGIN_PERSONALIZATION, { item_id: story.slug })
    trackMeta({
      event_name: META_EVENTS.LEAD,
      item_id: [story.slug],
      item_name: story.title,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Rehydrate the wizard from a persisted session UUID in the URL so a refresh
  // or returning visit resumes on the preview step without losing uploaded
  // photo + child details. Age group and book format aren't on the session
  // response, so walking back past `preview` will re-collect those.
  const sessionQueryParam = useMemo(() => {
    const value = router.query.session

    return typeof value === 'string' && value ? value : null
  }, [router.query.session])

  const claimActionParam = useMemo(() => {
    const value = router.query.action

    return typeof value === 'string' && value === 'claim'
  }, [router.query.action])

  const [isRehydrating, setIsRehydrating] = useState<boolean>(
    () => typeof window !== 'undefined' && new URL(window.location.href).searchParams.has('session'),
  )
  const autoClaimTriggeredRef = useRef(false)

  useEffect(() => {
    if (!router.isReady) return undefined

    if (!sessionQueryParam || sessionUuid) {
      setIsRehydrating(false)

      return undefined
    }

    let cancelled = false

    const dropSessionFromUrl = () => {
      const { session: _drop, ...rest } = router.query

      router.replace(
        { pathname: router.pathname, query: rest },
        undefined,
        { shallow: true },
      )
    }

    ;(async () => {
      try {
        const session = await storefrontSessionsRetrieve(sessionQueryParam)

        if (cancelled) return

        if (session.is_expired || session.status === 'CANCELLED' || session.status === 'CLAIMED') {
          dropSessionFromUrl()

          return
        }

        setSessionUuid(session.uuid)
        if (session.language_code) setSelectedLanguageCode(session.language_code)
        if (session.print_preset_id) setSelectedPresetId(session.print_preset_id)

        const child = session.children?.[0]

        if (child) {
          setChildName(child.name)

          if (child.photo_url) {
            setRoleInputs((prev) => ({
              ...prev,
              [primaryRoleKey]: {
                ...(prev[primaryRoleKey] ?? EMPTY_ROLE_INPUT),
                mode: 'upload',
                photoPreviewUrl: child.photo_url ?? null,
              },
            }))
          }
        }

        // Magic-link return path: `?action=claim` signals the user just
        // verified their magic link and wants to immediately turn this
        // session into a real story using their trial credits. The
        // auto-claim effect below picks this up once the credit balance
        // has loaded.
        if (claimActionParam) {
          setStep('claiming')
        } else {
          setStep('preview')
          setCoverStatus('pending')
        }
      } catch {
        if (cancelled) return

        dropSessionFromUrl()
      } finally {
        if (!cancelled) setIsRehydrating(false)
      }
    })()

    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, sessionQueryParam, claimActionParam])

  // Once the user is back from the magic-link verify page (authenticated)
  // and we've rehydrated their session, auto-claim and kick off generation.
  // The action=claim query param scopes this to magic-link returns so we
  // don't auto-claim on plain rehydrations. The backend handles credit
  // checks server-side and returns a 402 if the user is genuinely out.
  useEffect(() => {
    if (autoClaimTriggeredRef.current) return
    if (!router.isReady || !claimActionParam) return
    if (!sessionUuid) return
    if (!isAuthenticated) return
    if (step !== 'claiming') return

    autoClaimTriggeredRef.current = true

    // Drop ?action=claim from the URL so a refresh doesn't re-trigger.
    const { action: _drop, ...rest } = router.query

    router.replace(
      { pathname: router.pathname, query: rest },
      undefined,
      { shallow: true },
    )

    handleDirectGenerate(sessionUuid)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, claimActionParam, sessionUuid, isAuthenticated, step])

  // Bind PhotoUploadWidget's onPhotoSelect to the active role we're editing.
  // The widget itself is role-agnostic; the wizard threads role context in.
  const handlePhotoSelectFor = useCallback((roleKey: string) => (
    (file: File, previewUrl: string) => setRolePhoto(roleKey, file, previewUrl)
  ), [setRolePhoto])

  // Phase A: fetch the sample story's real cover once we have a sample slug,
  // so we can show it blurred behind a lock icon until the personalised cover
  // (Phase B) comes back.
  const sampleSlug = story.sample_story_share_slugs?.[0] ?? null

  useEffect(() => {
    if (!sampleSlug || sampleCoverUrl) return

    let cancelled = false

    publicStoriesDetail(sampleSlug)
      .then((res) => {
        if (cancelled) return

        const cover = res?.story?.cover_image_url

        if (cover) setSampleCoverUrl(cover)
      })
      .catch(() => {
        // Non-fatal: we'll fall back to story.thumbnail_url
      })

    return () => { cancelled = true }
  }, [sampleSlug, sampleCoverUrl])

  // Phase B: poll the personalised-cover status until it's ready or failed.
  useEffect(() => {
    if (!sessionUuid) return
    if (step !== 'preview') return
    if (coverStatus === 'ready' || coverStatus === 'failed') return

    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null

    const poll = async () => {
      try {
        const res = await storefrontSessionsPremadeCoverPreviewRetrieve(sessionUuid)

        if (cancelled) return

        setCoverStatus(res.status ?? 'pending')
        if (res.cover_url) setPersonalisedCoverUrl(res.cover_url)

        if (res.status !== 'ready' && res.status !== 'failed') {
          timer = setTimeout(poll, 2500)
        }
      } catch {
        if (!cancelled) {
          timer = setTimeout(poll, 5000)
        }
      }
    }

    timer = setTimeout(poll, 1500)

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [sessionUuid, step, coverStatus])

  const handleClose = useCallback(() => {
    router.push(`/shop/${story.slug}`)
  }, [router, story.slug])

  const isChildInfoComplete = childName.trim().length > 0 && childAgeGroup !== ''

  const handlePhotoNext = useCallback(() => {
    if (!canLeavePhotoStep) return
    setStep('child_info')

    if (!customizeProductFiredRef.current) {
      customizeProductFiredRef.current = true
      trackMeta({
        event_name: META_EVENTS.CUSTOMIZE_PRODUCT,
        item_id: [story.slug],
        item_name: story.title,
        item_category: 'premade-story',
      })
    }
  }, [canLeavePhotoStep, story.slug, story.title, trackMeta])

  const startCoverPreview = useCallback(async () => {
    if (!isChildInfoComplete || !canLeavePhotoStep) return

    setStep('preview')
    setIsPreparingPreview(true)
    setError(null)
    setCoverStatus('pending')
    setPersonalisedCoverUrl(null)

    try {
      // Reuse session if the user previously advanced then went back.
      let uuid = sessionUuid

      if (!uuid) {
        const session = await storefrontSessionsCreate({
          generation_mode: 'ai_generation',
          language_code: selectedLanguageCode,
        })

        uuid = session.uuid
        setSessionUuid(uuid)

        // Persist the session in the URL so a refresh or return visit can
        // resume the wizard without losing the cover the user is waiting on.
        router.replace(
          { pathname: router.pathname, query: { ...router.query, session: uuid } },
          undefined,
          { shallow: true },
        )

        // Per role: create a child entry (with or without photo) and collect
        // any look selections so configure-premade can persist them.
        const selectedRoleLooks: Record<string, number> = {}

        for (const role of requiredLookRoles) {
          const input = roleInputs[role.role_key]

          if (!input) continue

          const childPayload: AddChild = {
            name: childName,
            age: AGE_GROUP_TO_AGE[childAgeGroup as AgeGroupEnum],
            gender: 'other' as Gender881Enum,
            role_key: role.role_key,
          }

          if (input.mode === 'upload' && input.photoFile) {
            childPayload.photo = input.photoFile
          }

          // eslint-disable-next-line no-await-in-loop
          await storefrontSessionsChildrenCreate(uuid, childPayload)

          if (input.mode === 'pick' && input.lookId) {
            selectedRoleLooks[role.role_key] = input.lookId
          }
        }

        await storefrontSessionsConfigurePremadeCreate(uuid, {
          selected_role_looks: selectedRoleLooks,
          premade_story_id: story.id,
          language_code: selectedLanguageCode,
        })
      }

      const res = await storefrontSessionsPremadeCoverPreviewCreate(uuid, {
        premade_story_id: story.id,
        print_preset_id: selectedPresetId ?? undefined,
      })

      setCoverStatus(res.status ?? 'pending')

      if (res.cover_url) {
        setPersonalisedCoverUrl(res.cover_url)
      }
    } catch (err) {
      const message = err instanceof Error
        ? err.message
        : 'We couldn\'t start your preview. You can still continue to checkout.'

      setError(message)
      setCoverStatus('failed')
    } finally {
      setIsPreparingPreview(false)
    }
  }, [
    isChildInfoComplete, canLeavePhotoStep, sessionUuid, childName, childAgeGroup,
    selectedLanguageCode, selectedPresetId, story.id, router,
    requiredLookRoles, roleInputs,
  ])

  const handleChildInfoNext = useCallback(() => {
    if (!isChildInfoComplete || !canLeavePhotoStep) return

    setStep(hasLanguageChoice ? 'language' : 'format')
  }, [isChildInfoComplete, canLeavePhotoStep, hasLanguageChoice])

  const handleLanguageNext = useCallback(() => {
    setStep('format')
  }, [])

  const handleFormatNext = useCallback(() => {
    if (selectedPresetId === null) return

    startCoverPreview()
  }, [selectedPresetId, startCoverPreview])

  const handleDirectGenerate = useCallback(async (sessionUuidOverride?: string) => {
    const uuid = sessionUuidOverride ?? sessionUuid

    if (!uuid) {
      setError('Session not ready. Please go back and try again.')

      return
    }

    setIsProcessing(true)
    setError(null)
    setStep('claiming')

    try {
      const res = await storefrontSessionsClaimAndGenerateCreate(uuid)

      if (res?.story_id) {
        // Hand off to /library/{storyId} where the user can watch generation
        // finish and order a hardcover via the existing PrintOrderDialog if
        // they want one — no in-wizard upsell.
        router.push(`/library/${res.story_id}`)
      } else {
        throw new Error('Story generation did not return an ID.')
      }
    } catch (err) {
      setError(extractApiError(err, 'We couldn\'t start your story. Please try again.'))
      setStep('preview')
    } finally {
      setIsProcessing(false)
    }
  }, [sessionUuid, router])

  const handlePreviewNext = useCallback(() => {
    // Authenticated users go straight to claim. The backend checks credits
    // server-side and returns a friendly 402 if the user is genuinely out
    // (which we surface in handleDirectGenerate's catch). Anonymous users
    // go through the email gate first; magic-link return triggers an
    // auto-claim via the effect above.
    if (isAuthenticated) {
      handleDirectGenerate()

      return
    }

    setStep('email')
    trackEvent(GOOGLE_EVENTS.BEGIN_CHECKOUT, { item_id: story.slug })
    trackMeta({
      event_name: META_EVENTS.INITIATE_CHECKOUT,
      item_id: [story.slug],
      item_name: story.title,
    })
  }, [isAuthenticated, handleDirectGenerate, story.slug, story.title, trackMeta])

  const handleSendMagicLink = useCallback(async () => {
    const trimmed = emailInput.trim()

    if (!trimmed) return

    // Cheap client-side check so trivial typos (missing @, no domain) get
    // immediate feedback without an HTTP round-trip. The backend remains
    // authoritative for stricter validation.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('That email address doesn\'t look quite right. Please double-check and try again.')

      return
    }

    if (!sessionUuid) {
      setError('Session not ready. Please go back and try again.')

      return
    }

    setIsProcessing(true)
    setError(null)

    try {
      // Round-trip the user through /auth/verify and back to the wizard so
      // the session UUID + claim intent survive across the email click. The
      // backend's send_magic_link_email tolerates an existing query string
      // on redirect_url (uses & instead of ? for the appended token).
      const returnPath = `${router.asPath.split('?')[0]}?session=${sessionUuid}&action=claim`
      const redirectUrl = `${window.location.origin}/auth/verify?next=${encodeURIComponent(returnPath)}`

      await requestMagicLink.mutateAsync({
        data: { email: trimmed, redirect_url: redirectUrl },
      })

      setStep('email_sent')
    } catch (err) {
      setError(extractApiError(err, 'We couldn\'t send the magic link. Please try again.'))
    } finally {
      setIsProcessing(false)
    }
  }, [emailInput, sessionUuid, requestMagicLink, router])

  const handleBack = useCallback(() => {
    if (step === 'child_info') setStep('photo')
    else if (step === 'language') setStep('child_info')
    else if (step === 'format') setStep(hasLanguageChoice ? 'language' : 'child_info')
    else if (step === 'preview') setStep('format')
    else if (step === 'email') setStep('preview')
  }, [step, hasLanguageChoice])

  const showBackButton = step === 'child_info' || step === 'language'
    || step === 'format' || step === 'preview' || step === 'email'

  const isAnyRoleUploading = useMemo(
    () => Object.values(roleInputs).some((input) => input.mode === 'upload'),
    [roleInputs],
  )

  const baseSteps: Step[] = hasLanguageChoice
    ? ['photo', 'child_info', 'language', 'format', 'preview']
    : ['photo', 'child_info', 'format', 'preview']
  // Step counter ladder:
  //  - signed-in users end at preview (the next click claims directly)
  //  - anonymous users get an extra email step before the claim
  const visibleSteps: Step[] = isAuthenticated
    ? baseSteps
    : [...baseSteps, 'email']
  const fallbackStep = visibleSteps[visibleSteps.length - 1]
  const isTerminalStep = step === 'email_sent' || step === 'claiming'
  const currentStepIndex = Math.max(0, visibleSteps.indexOf(
    isTerminalStep ? fallbackStep : step,
  )) + 1
  const totalSteps = visibleSteps.length

  // Preview cover pick order: personalised (when ready) → sample story cover
  // (from publicStoriesDetail) → static product thumbnail.
  const isPersonalisedReady = coverStatus === 'ready' && !!personalisedCoverUrl
  const displayCoverUrl = isPersonalisedReady
    ? personalisedCoverUrl
    : (sampleCoverUrl || story.thumbnail_url || '')
  const isGenerating = isPreparingPreview
    || coverStatus === 'pending'
    || coverStatus === 'generating'

  // Time-based progress so the user has something to watch while we wait
  // for the backend to return the personalised cover.
  useEffect(() => {
    if (step !== 'preview') {
      generationStartRef.current = null
      setProgressValue(0)

      return undefined
    }

    if (coverStatus === 'ready') {
      setProgressValue(100)

      return undefined
    }

    if (coverStatus === 'failed') {
      return undefined
    }

    if (generationStartRef.current === null) {
      generationStartRef.current = Date.now()
    }

    const tick = () => {
      const start = generationStartRef.current ?? Date.now()
      const elapsed = Date.now() - start
      const ratio = elapsed / COVER_GENERATION_ESTIMATE_MS

      setProgressValue(Math.min(95, ratio * 100))
    }

    tick()

    const id = window.setInterval(tick, 300)

    return () => {
      window.clearInterval(id)
    }
  }, [step, coverStatus])

  if (isRehydrating) {
    return (
      <StyledFullPage>
        <StyledHeader>
          <Typography variant="h6" fontWeight={600}>
            {story.title}
          </Typography>
        </StyledHeader>
        <StyledContent>
          <StyledLoadingState>
            <CircularProgress size={48} />
            <Typography variant="body1" color="text.secondary" sx={{ mt: 3 }}>
              Restoring your progress…
            </Typography>
          </StyledLoadingState>
        </StyledContent>
      </StyledFullPage>
    )
  }

  return (
    <StyledFullPage>
      {step === 'photo' && isAnyRoleUploading && <PhotoTrustDialog />}
      <StyledHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {showBackButton && (
            <IconButton onClick={handleBack} size="small" aria-label="Back">
              <Icon icon={faArrowLeft} />
            </IconButton>
          )}
          <Typography variant="h6" fontWeight={600}>
            {story.title}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" aria-label="Close">
          <Icon icon={faXmark} />
        </IconButton>
      </StyledHeader>

      <StyledContent>
        <Container maxWidth="md">
          <StyledStepContainer>
            <Typography variant="overline" color="text.secondary" sx={{ mb: 1 }}>
              {`Step ${currentStepIndex} of ${totalSteps}`}
            </Typography>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              {STEP_TITLES[step]}
            </Typography>

            {step === 'photo' && (
              <>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Choose how your
                  {requiredLookRoles.length > 1 ? ' characters appear ' : ' child appears '}
                  in the book. Pick a stylised character, or
                  upload a photo for the closest likeness.
                </Typography>

                {requiredLookRoles.map((role, idx) => {
                  const input = roleInputs[role.role_key] ?? EMPTY_ROLE_INPUT
                  const looksForRole = looksByRole[role.role_key] ?? []
                  const hasLooks = looksForRole.length > 0
                  const isMultiRole = requiredLookRoles.length > 1

                  return (
                    <Box
                      key={role.role_key}
                      sx={{ mt: idx === 0 ? 0 : 4, mb: 1 }}
                    >
                      {isMultiRole && (
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                          {role.display_name}
                        </Typography>
                      )}

                      {hasLooks && (
                        <Tabs
                          value={input.mode}
                          onChange={(_, v: RoleInputMode) => setRoleInputMode(role.role_key, v)}
                          sx={{ mb: 2 }}
                        >
                          <Tab value="pick" label="Pick a character" />
                          <Tab value="upload" label="Upload a photo" />
                        </Tabs>
                      )}

                      {input.mode === 'pick' && hasLooks && (
                        <PremadeCharacterPicker
                          role={role}
                          looks={looksForRole}
                          selectedLookId={input.lookId}
                          onSelect={(lookId) => setRoleLook(role.role_key, lookId)}
                          onCreateCustom={() => setRoleInputMode(role.role_key, 'upload')}
                        />
                      )}

                      {(input.mode === 'upload' || !hasLooks) && (
                        <PhotoUploadWidget
                          onPhotoSelect={handlePhotoSelectFor(role.role_key)}
                          currentPreviewUrl={input.photoPreviewUrl}
                          onRetry={() => clearRolePhoto(role.role_key)}
                        />
                      )}
                    </Box>
                  )
                })}

                <StyledActionBar>
                  <Button
                    primary
                    size="large"
                    fullWidth
                    disabled={!canLeavePhotoStep}
                    onClick={handlePhotoNext}
                  >
                    Next
                    <Icon icon={faArrowRight} style={{ marginLeft: 8 }} />
                  </Button>
                </StyledActionBar>
              </>
            )}

            {step === 'child_info' && (
              <>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  We&apos;ll use this to personalise the story.
                </Typography>
                <StyledForm>
                  <TextField
                    label="Child's name"
                    value={childName}
                    onChange={(e) => setChildName(e.target.value)}
                    size="large"
                    required
                    inputProps={{ maxLength: 100 }}
                  />
                  <Box>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                      Age group *
                    </Typography>
                    <StyledOptionGroup>
                      {allowedAgeGroups.map((option) => (
                        <StyledOptionButton
                          key={option.value}
                          type="button"
                          $selected={childAgeGroup === option.value}
                          onClick={() => setChildAgeGroup(option.value)}
                        >
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {option.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.description}
                          </Typography>
                        </StyledOptionButton>
                      ))}
                    </StyledOptionGroup>
                  </Box>
                </StyledForm>
                <StyledActionBar>
                  <Button
                    primary
                    size="large"
                    fullWidth
                    disabled={!isChildInfoComplete}
                    onClick={handleChildInfoNext}
                  >
                    Next
                    <Icon icon={faArrowRight} style={{ marginLeft: 8 }} />
                  </Button>
                </StyledActionBar>
              </>
            )}

            {step === 'language' && (
              <>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Pick the language your storybook will be written in.
                </Typography>
                <StyledOptionGroup>
                  {availableLanguages.map((lang) => {
                    const flag = getLanguageFlag(lang.code)

                    return (
                      <StyledOptionButton
                        key={lang.code}
                        type="button"
                        $selected={selectedLanguageCode === lang.code}
                        onClick={() => setSelectedLanguageCode(lang.code)}
                      >
                        <StyledLanguageRow>
                          {flag && <StyledLanguageFlag aria-hidden>{flag}</StyledLanguageFlag>}
                          <StyledLanguageLabels>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {lang.name_native}
                            </Typography>
                            {lang.name_english !== lang.name_native && (
                              <Typography variant="caption" color="text.secondary">
                                {lang.name_english}
                              </Typography>
                            )}
                          </StyledLanguageLabels>
                        </StyledLanguageRow>
                      </StyledOptionButton>
                    )
                  })}
                </StyledOptionGroup>
                <StyledActionBar>
                  <Button
                    primary
                    size="large"
                    fullWidth
                    onClick={handleLanguageNext}
                  >
                    Next
                    <Icon icon={faArrowRight} style={{ marginLeft: 8 }} />
                  </Button>
                </StyledActionBar>
              </>
            )}

            {step === 'format' && (
              <>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  Pick the size and shape of your book. The story stays the same — only how each spread is laid out changes.
                </Typography>
                <StyledFormatInfoBox>
                  <Icon icon={faCircleInfo} style={{ fontSize: 20, color: '#1976d2', marginTop: 2 }} />
                  <Box>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                      What is book format?
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="p">
                      Format is the dimensions of your book pages, in inches (width × height). It shapes how each spread looks — square feels balanced and modern, portrait reads like a classic storybook, and landscape gives wide scenes more room to breathe. Illustrations and text re-flow to fit whichever format you choose, and this is how your book will look whether you read it in the app or order a printed copy.
                    </Typography>
                  </Box>
                </StyledFormatInfoBox>
                {isPresetsLoading || bookFormatPresets.length === 0 ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <BookFormatSelector
                    presets={bookFormatPresets}
                    selectedPresetId={selectedPresetId}
                    onSelect={(id) => setSelectedPresetId(id)}
                  />
                )}
                <StyledActionBar>
                  <Button
                    primary
                    size="large"
                    fullWidth
                    disabled={selectedPresetId === null}
                    onClick={handleFormatNext}
                  >
                    Next
                    <Icon icon={faArrowRight} style={{ marginLeft: 8 }} />
                  </Button>
                </StyledActionBar>
              </>
            )}

            {step === 'preview' && (
              <>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  {isPersonalisedReady
                    ? 'Here\'s your personalised story. The inside pages unlock after checkout.'
                    : isGenerating
                      ? 'We\'re drawing your personalised story — this usually takes 20-40 seconds. Please keep this page open.'
                      : coverStatus === 'failed'
                        ? 'We couldn\'t generate your preview this time, but you can still continue to checkout.'
                        : 'Here\'s a peek at the story. The rest of the pages unlock after checkout.'}
                </Typography>
                <StyledPreviewStack>
                  <StyledCoverFrame style={{ aspectRatio: coverAspectRatio }}>
                    {displayCoverUrl ? (
                      <StyledCoverImageWrapper>
                        <CustomImage
                          src={displayCoverUrl}
                          alt={story.title}
                          noRatio
                          blurImage={isGenerating}
                          unoptimized
                        />
                      </StyledCoverImageWrapper>
                    ) : (
                      <StyledPlaceholderCover>
                        <Typography variant="h4" color="white">
                          {story.title.substring(0, 2).toUpperCase()}
                        </Typography>
                      </StyledPlaceholderCover>
                    )}
                    {isGenerating && (
                      <StyledQuillOverlay>
                        <QuillIcon size={96} animated />
                      </StyledQuillOverlay>
                    )}
                  </StyledCoverFrame>
                  {isGenerating && (
                    <StyledProgressWrapper>
                      <LinearProgress
                        variant="determinate"
                        value={progressValue}
                        sx={{
                          width: '100%',
                          height: pxToRem(8),
                          borderRadius: pxToRem(4),
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Generating preview… {Math.round(progressValue)}%
                      </Typography>
                    </StyledProgressWrapper>
                  )}
                  <StyledBlurredPagesNote>
                    <Typography variant="body2" color="text.secondary">
                      +
                      {' '}
                      {insidePagesCount ?? 'more'}
                      {' '}
                      personalised pages inside
                    </Typography>
                  </StyledBlurredPagesNote>
                </StyledPreviewStack>
                <StyledActionBar>
                  <Button
                    primary
                    size="large"
                    fullWidth
                    disabled={isGenerating || isProcessing}
                    onClick={handlePreviewNext}
                  >
                    {isGenerating
                      ? 'Generating preview…'
                      : isAuthenticated
                        ? 'Create my story'
                        : 'Claim your free story'}
                    {!isGenerating && <Icon icon={faArrowRight} style={{ marginLeft: 8 }} />}
                  </Button>
                </StyledActionBar>
              </>
            )}

            {step === 'email' && (
              <>
                {error && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </Alert>
                )}
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Tell us where to send the link. We&apos;ll save your story to your library
                  and create your first one on the house — no payment needed.
                </Typography>
                <StyledForm
                  as="form"
                  onSubmit={(e: React.FormEvent) => {
                    e.preventDefault()
                    handleSendMagicLink()
                  }}
                >
                  <TextField
                    label="Email address"
                    type="email"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    size="large"
                    required
                    autoFocus
                    inputProps={{ inputMode: 'email', autoComplete: 'email' }}
                  />
                  <StyledFreeStoryNote>
                    <Icon icon={faCheckCircle} style={{ fontSize: 18, color: '#4caf50', marginTop: 2 }} />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        Your first story is on us
                      </Typography>
                      <Typography variant="caption" color="text.secondary" component="p">
                        New users get one free personalised storybook. We&apos;ll send a
                        magic link to sign you in.
                      </Typography>
                    </Box>
                  </StyledFreeStoryNote>
                </StyledForm>
                <StyledActionBar>
                  <Button
                    primary
                    size="large"
                    fullWidth
                    disabled={!emailInput.trim() || isProcessing}
                    onClick={handleSendMagicLink}
                  >
                    {isProcessing ? 'Sending…' : 'Email me my story'}
                    {!isProcessing && <Icon icon={faArrowRight} style={{ marginLeft: 8 }} />}
                  </Button>
                </StyledActionBar>
              </>
            )}

            {step === 'email_sent' && (
              <StyledLoadingState>
                <Icon
                  icon={faEnvelope}
                  style={{ fontSize: 64, color: '#1976d2', marginBottom: 16 }}
                />
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  Check your email
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 1, maxWidth: pxToRem(420) }}>
                  We&apos;ve sent a magic link to <strong>{emailInput}</strong>.
                  Click it to sign in and we&apos;ll create your story right away.
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 4 }}>
                  The link expires in 15 minutes. Keep this tab open if you want — but
                  you can also click the link from any device.
                </Typography>
                <Button
                  size="large"
                  disabled={isProcessing}
                  onClick={handleSendMagicLink}
                >
                  Resend magic link
                </Button>
              </StyledLoadingState>
            )}

            {step === 'claiming' && (
              <StyledLoadingState>
                <CircularProgress size={48} />
                <Typography variant="h6" sx={{ mt: 3 }}>
                  Creating your story…
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, maxWidth: pxToRem(420) }}>
                  This usually takes around a minute. We&apos;ll take you to the next step
                  as soon as it&apos;s ready.
                </Typography>
              </StyledLoadingState>
            )}

          </StyledStepContainer>
        </Container>
      </StyledContent>
    </StyledFullPage>
  )
}

const StyledFullPage = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'white',
  zIndex: 1300,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
})

const StyledHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: `${pxToRem(12)} ${pxToRem(24)}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  flexShrink: 0,
}))

const StyledContent = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  padding: `${pxToRem(32)} 0`,
})

const StyledStepContainer = styled(Box)({
  maxWidth: pxToRem(560),
  margin: '0 auto',
})

const StyledForm = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: pxToRem(20),
})

const StyledOptionGroup = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: pxToRem(8),
})

const StyledOptionButton = styled('button', {
  shouldForwardProp: (prop) => prop !== '$selected',
})<{ $selected: boolean }>(({ theme, $selected }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: pxToRem(2),
  padding: `${pxToRem(14)} ${pxToRem(16)}`,
  border: `2px solid ${$selected ? theme.palette.primary.main : theme.palette.divider}`,
  borderRadius: pxToRem(12),
  backgroundColor: $selected ? `${theme.palette.primary.main}15` : theme.palette.background.paper,
  cursor: 'pointer',
  transition: 'all 0.2s',
  textAlign: 'left',

  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
}))

const StyledLanguageRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: pxToRem(12),
  width: '100%',
})

const StyledLanguageFlag = styled('span')({
  fontSize: pxToRem(22),
  lineHeight: 1,
  flexShrink: 0,
})

const StyledLanguageLabels = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: pxToRem(2),
  minWidth: 0,
})

const StyledActionBar = styled(Box)({
  marginTop: pxToRem(32),
  paddingBottom: pxToRem(32),
})

const StyledFormatInfoBox = styled(Box)({
  display: 'flex',
  gap: pxToRem(12),
  alignItems: 'flex-start',
  padding: `${pxToRem(12)} ${pxToRem(16)}`,
  backgroundColor: '#e8f4ff',
  borderRadius: pxToRem(8),
  marginBottom: pxToRem(20),
})

const StyledLoadingState = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '40vh',
  textAlign: 'center',
})

const StyledPreviewStack = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: pxToRem(16),
})

const StyledCoverFrame = styled(Box)({
  width: '100%',
  maxWidth: pxToRem(420),
  borderRadius: pxToRem(4),
  overflow: 'hidden',
  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
  position: 'relative',
  backgroundColor: 'rgba(0,0,0,0.04)',
})

const StyledPlaceholderCover = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  background: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}))

const StyledBlurredPagesNote = styled(Box)({
  textAlign: 'center',
})

const StyledCoverImageWrapper = styled(Box)({
  position: 'relative',
  width: '100%',
  height: '100%',
})

const StyledQuillOverlay = styled(Box)({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
  zIndex: 1,
})

const StyledProgressWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: pxToRem(6),
  width: '100%',
  maxWidth: pxToRem(320),
})

const StyledFreeStoryNote = styled(Box)({
  display: 'flex',
  gap: pxToRem(12),
  alignItems: 'flex-start',
  padding: `${pxToRem(12)} ${pxToRem(16)}`,
  backgroundColor: '#e8f5e9',
  borderRadius: pxToRem(8),
})

